import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, ShieldCheck, Sparkles, TreePine, Coffee, Dumbbell, Waves, MapPin, ChevronLeft, ChevronRight, Award, Heart, Compass, Target } from "lucide-react";
import { api, WHATSAPP_LINK } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ProjectCard } from "@/components/ProjectCard";
import { resolveImageUrl } from "@/components/ImageUploader";
import { useSiteContent } from "@/context/SiteContentContext";

// Icon registry — kept frontend-side so admin only edits string keys
const ICONS = {
  Building2, ShieldCheck, Sparkles, TreePine, Coffee, Dumbbell, Waves, Award, Heart, Compass, Target,
};
const Icon = ({ name, ...rest }) => {
  const Cmp = ICONS[name] || Building2;
  return <Cmp {...rest} />;
};

export default function Home() {
  const { content } = useSiteContent();
  const c = content.home;
  const [projects, setProjects] = useState([]);
  const carouselRef = useRef(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
  };

  const heroProject = projects.find((p) => p.status === "devam_ediyor") || projects[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          {heroProject ? (
            <img src={resolveImageUrl(heroProject.hero_image)} alt={heroProject.title} className="w-full h-full object-cover scale-[1.04]" />
          ) : (
            <div className="w-full h-full bg-[var(--stone)]" />
          )}
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="pointer-events-none absolute -bottom-32 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full border border-[var(--gold)]/15" />
        <div className="pointer-events-none absolute top-32 right-10 w-40 h-40 border border-[var(--gold)]/25 rounded-tl-full hidden lg:block" />

        <div className="relative z-10 min-h-screen flex items-end pb-24 lg:pb-32 px-6 lg:px-12">
          <div className="max-w-7xl w-full mx-auto">
            <Reveal>
              <span className="eyebrow text-[var(--gold)]">{c.hero.eyebrow}</span>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="mt-5 font-serif-luxe text-[var(--ivory)] text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl">
                {c.hero.title_line1}
                <span className="block italic text-[var(--gold)] font-light">{c.hero.title_italic}</span>
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-6 text-white max-w-xl text-base lg:text-lg font-light leading-relaxed" style={{ textShadow: "0 2px 12px rgba(10, 25, 47, 0.6)" }}>
                {c.hero.subtitle}
              </p>
            </Reveal>
            <Reveal delay={360}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/projeler" className="btn-primary" data-testid="hero-cta-projects">
                  {c.hero.cta1_label} <ArrowRight size={16} />
                </Link>
                <Link to="/iletisim" className="btn-ghost-gold" style={{ color: "var(--ivory)", borderColor: "var(--gold)" }} data-testid="hero-cta-contact">
                  {c.hero.cta2_label}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[var(--ivory)]/60 text-xs tracking-[0.3em] uppercase">{c.hero.scroll_hint}</div>
      </section>

      {/* BIZ KIMIZ */}
      <section className="relative bg-[var(--ivory)] py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
        <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 border border-[var(--gold)]/15 rounded-full" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <span className="eyebrow">{c.about.eyebrow}</span>
            <h2 className="font-serif-luxe text-4xl lg:text-5xl mt-4 text-[var(--navy)] leading-tight">
              {c.about.heading_line1} <span className="italic text-[var(--gold)]">{c.about.heading_italic}</span> {c.about.heading_line2}
            </h2>
            <div className="gold-divider w-24 my-6" />
            <p className="text-[var(--slate)] leading-relaxed font-light text-base lg:text-lg max-w-xl whitespace-pre-line">{c.about.body}</p>
            <Link to="/hakkimizda" className="inline-flex items-center gap-2 mt-8 text-[var(--navy)] hover:text-[var(--gold)] transition-colors text-sm tracking-[0.22em] uppercase border-b border-[var(--gold)] pb-1" data-testid="home-about-link">
              {c.about.link_label} <ArrowRight size={14} />
            </Link>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={150}>
            <div className="relative arch-frame">
              <div className="relative overflow-hidden arch-top">
                <img src={resolveImageUrl(c.about.image)} alt="Özkayalar mimari estetik" className="w-full h-[520px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-40 h-40 border-2 border-[var(--gold)]/60 rounded-tl-full pointer-events-none" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="relative bg-[var(--cream)] py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6">
          {c.stats.map((s, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="lux-card p-8 lg:p-10 text-center relative overflow-hidden" data-testid={`stat-${i}`}>
                <span className="absolute top-3 right-3 w-7 h-7 border-t border-r border-[var(--gold)]/50" />
                <div className="font-serif-luxe text-5xl lg:text-6xl text-[var(--navy)]">{s.number}</div>
                <div className="gold-divider w-12 mx-auto my-4" />
                <div className="text-sm tracking-[0.18em] uppercase text-[var(--ink)]">{s.label}</div>
                <div className="text-xs text-[var(--slate)] mt-2 font-light">{s.note}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROJELER */}
      <section className="relative bg-[var(--ivory)] py-24 lg:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-end justify-between mb-10">
          <Reveal>
            <span className="eyebrow">{c.projects_section.eyebrow}</span>
            <h2 className="font-serif-luxe text-4xl lg:text-5xl mt-3 text-[var(--navy)]">
              {c.projects_section.heading_line1} <span className="italic text-[var(--gold)]">{c.projects_section.heading_italic}</span>
            </h2>
          </Reveal>
          <div className="hidden lg:flex items-center gap-3">
            <button onClick={() => scrollCarousel(-1)} aria-label="prev" data-testid="carousel-prev" className="w-11 h-11 border border-[var(--navy)]/30 hover:border-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--ivory)] text-[var(--navy)] transition-colors flex items-center justify-center"><ChevronLeft size={18} /></button>
            <button onClick={() => scrollCarousel(1)} aria-label="next" data-testid="carousel-next" className="w-11 h-11 border border-[var(--navy)]/30 hover:border-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--ivory)] text-[var(--navy)] transition-colors flex items-center justify-center"><ChevronRight size={18} /></button>
          </div>
        </div>

        <div ref={carouselRef} className="no-scrollbar overflow-x-auto px-6 lg:px-12 pb-6">
          <div className="flex gap-6 lg:gap-8 min-w-min">
            {projects.map((p) => (
              <div key={p.id} className="shrink-0 w-[300px] sm:w-[380px] lg:w-[460px]">
                <ProjectCard project={p} variant="wide" />
              </div>
            ))}
            {projects.length === 0 && <div className="text-[var(--slate)] px-4 py-12">{c.projects_section.loading_text}</div>}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-10">
          <Link to="/projeler" className="btn-ghost-gold inline-flex" data-testid="home-all-projects">
            {c.projects_section.view_all_label} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* NEDEN ÖZKAYALAR */}
      <section className="relative bg-[var(--navy)] text-[var(--ivory)] py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
        <div className="pointer-events-none absolute -top-32 right-0 w-[600px] h-[600px] rounded-full border border-[var(--gold)]/15" />
        <div className="pointer-events-none absolute bottom-10 left-10 w-24 h-24 border border-[var(--gold)]/40 rounded-tl-full" />
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="eyebrow">{c.why_us.eyebrow}</span>
            <h2 className="font-serif-luxe text-4xl lg:text-5xl mt-3 max-w-2xl">
              {c.why_us.heading_line1} <span className="italic text-[var(--gold)]">{c.why_us.heading_italic}</span>
            </h2>
            <div className="gold-divider w-24 my-6" />
            <p className="max-w-2xl text-[var(--ivory)]/80 leading-relaxed font-light whitespace-pre-line">{c.why_us.body}</p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {c.why_us.features.map((f, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="relative p-8 border border-[var(--gold)]/25 hover:border-[var(--gold)]/70 transition-colors group" data-testid={`feature-${i}`}>
                  <div className="w-12 h-12 mb-6 border border-[var(--gold)] flex items-center justify-center text-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-[var(--navy)] transition-colors">
                    <Icon name={f.icon} size={20} />
                  </div>
                  <h3 className="font-serif-luxe text-2xl mb-3">{f.title}</h3>
                  <p className="text-sm text-[var(--ivory)]/75 font-light leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOSYAL */}
      <section className="relative bg-[var(--ivory)] py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7" delay={50}>
            <div className="relative">
              <div className="overflow-hidden arch-top">
                <img src={resolveImageUrl(c.social.image)} alt="Sosyal alanlar" className="w-full h-[560px] object-cover" />
              </div>
              <div className="absolute top-6 left-6 w-24 h-24 border border-[var(--gold)] rounded-tl-full pointer-events-none" />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={150}>
            <span className="eyebrow">{c.social.eyebrow}</span>
            <h2 className="font-serif-luxe text-4xl lg:text-5xl mt-3 text-[var(--navy)]">
              {c.social.heading_line1} <span className="italic text-[var(--gold)]">{c.social.heading_italic}</span>
            </h2>
            <div className="gold-divider w-24 my-6" />
            <p className="text-[var(--slate)] leading-relaxed font-light max-w-md whitespace-pre-line">{c.social.body}</p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {c.social.amenities.map((s, i) => (
                <div key={i} className="flex items-center gap-3 p-4 border border-[var(--gold)]/30 bg-[var(--cream)]/60" data-testid={`amenity-${s.label}`}>
                  <Icon name={s.icon} size={18} className="text-[var(--gold)]" />
                  <span className="text-sm tracking-[0.16em] uppercase text-[var(--navy)]">{s.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[var(--cream)] py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
        <div className="pointer-events-none absolute -top-20 right-1/2 translate-x-1/2 w-[800px] h-[800px] rounded-full border border-[var(--gold)]/15" />
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="eyebrow">{c.final_cta.eyebrow}</span>
            <h2 className="font-serif-luxe text-4xl lg:text-5xl mt-3 text-[var(--navy)]">
              {c.final_cta.heading_line1} <span className="italic text-[var(--gold)]">{c.final_cta.heading_italic}</span>
            </h2>
            <p className="mt-6 text-[var(--slate)] leading-relaxed font-light max-w-xl mx-auto whitespace-pre-line">{c.final_cta.body}</p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link to="/iletisim" className="btn-primary" data-testid="cta-contact">{c.final_cta.cta1_label}</Link>
              <a href={WHATSAPP_LINK(content.contact.default_whatsapp_message, content.contact.whatsapp_number)} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold" data-testid="cta-whatsapp">
                {c.final_cta.cta2_label}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
